/**
 * 個別説明日程確定ダイアログ。
 * 申込中の Deal に対して、briefing_schedules から枠を選んで確定する。
 */
import type { BriefingBookingStyle } from '@goenhub/shared';
import { BRIEFING_STYLE_LABEL } from '@goenhub/shared';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useAdminDirectory } from '@/hooks/useAdmins';
import {
  useBriefingSchedules,
  useConfirmBriefingDate,
} from '@/hooks/useBriefings';

interface Props {
  open: boolean;
  dealId: string | null;
  /** フォーム送信時に選ばれた候補 scheduleId 群。これらをハイライト表示する */
  candidateScheduleIds?: string[];
  /**
   * 確定モード (2026-08-18)。
   *  - 'additional': 新しい個別説明セッションとして追加 (既存の確定日程は保持)
   *  - 'reschedule': replaceScheduleId のセッションを日程変更 (旧枠を解放)
   */
  mode?: 'additional' | 'reschedule';
  /** mode='reschedule' のとき、置き換える対象セッションの枠 id。 */
  replaceScheduleId?: string | null;
  /**
   * 商談に紐づく SA (外部営業) の uid。
   * 指定時は既定でその SA が登録した枠 (assigneeUid 一致) のみ表示する。
   * null/undefined (SA 紐付けなし) の場合は全担当者の枠を表示 (従来動作)。
   */
  salesAgentUid?: string | null;
  onOpenChange: (open: boolean) => void;
}

function formatJpDate(ymd: string): string {
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return ymd;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const days = ['日', '月', '火', '水', '木', '金', '土'];
  return `${m[1]}/${m[2]}/${m[3]} (${days[d.getDay()]})`;
}

export function ConfirmBriefingDateDialog({
  open,
  dealId,
  candidateScheduleIds,
  mode,
  replaceScheduleId,
  salesAgentUid,
  onOpenChange,
}: Props) {
  const { data: schedules, isLoading } = useBriefingSchedules();
  const { data: admins } = useAdminDirectory();
  const confirmMutation = useConfirmBriefingDate();
  const [selected, setSelected] = useState<string | null>(null);
  /** 「両方可」枠を選んだときの開催スタイル (確定時に必須)。 */
  const [selectedStyle, setSelectedStyle] =
    useState<BriefingBookingStyle | null>(null);
  /** SA 紐付けありでも敢えて全担当者の枠を表示するフラグ (退避用) */
  const [showAll, setShowAll] = useState(false);
  // 実施済みの回を後から追加する場合に過去日の枠も選べるようにする (2026-08-18)
  const [includePast, setIncludePast] = useState(false);

  // 開き直すたびに絞り込み状態と選択をリセット (前回の商談の状態を持ち越さない)
  useEffect(() => {
    if (open) {
      setShowAll(false);
      setIncludePast(false);
      setSelected(null);
      setSelectedStyle(null);
    }
  }, [open, dealId]);

  const today = new Date();
  const todayYmd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const available = (schedules ?? []).filter(
    (s) =>
      s.isActive &&
      // 1枠=1予約。予約済み (bookedCount>0) の枠はクローズ扱いで出さない。
      (s.bookedCount ?? 0) === 0 &&
      // 既定は今日以降。追加モードでは「過去の枠も表示」で実施済みの回を後から登録できる
      (includePast || s.date >= todayYmd),
  );

  // 商談に SA が紐づいている場合は、その SA が登録した枠のみに絞り込む。
  // (紐付けなし = 従来どおり全枠)
  const saFilterActive = !!salesAgentUid && !showAll;
  const filtered = saFilterActive
    ? available.filter((s) => s.assigneeUid === salesAgentUid)
    : available;

  const assigneeName = (uid: string | null | undefined) => {
    if (!uid) return '';
    const a = admins?.find((x) => x.uid === uid);
    return a?.fullName || a?.displayName || '';
  };
  const saName = assigneeName(salesAgentUid);

  // 候補をハイライト用にソート
  const sorted = [...filtered].sort((a, b) => {
    const aIsCand = candidateScheduleIds?.includes(a.id) ? 1 : 0;
    const bIsCand = candidateScheduleIds?.includes(b.id) ? 1 : 0;
    if (aIsCand !== bIsCand) return bIsCand - aIsCand;
    if (a.date !== b.date) return a.date.localeCompare(b.date);
    return a.startTime.localeCompare(b.startTime);
  });

  const selectedSlot = sorted.find((s) => s.id === selected) ?? null;
  const needsStyleChoice = selectedSlot?.style === 'both';

  const handleConfirm = async () => {
    if (!dealId || !selected) return;
    if (needsStyleChoice && !selectedStyle) return;
    try {
      await confirmMutation.mutateAsync({
        dealId,
        briefingScheduleId: selected,
        ...(selectedStyle ? { selectedStyle } : {}),
        ...(mode ? { mode } : {}),
        ...(replaceScheduleId ? { replaceScheduleId } : {}),
      });
      toast.success(
        mode === 'additional'
          ? '個別説明を追加しました (既存の日程はそのまま残ります)'
          : '個別説明日程を確定しました',
      );
      onOpenChange(false);
      setSelected(null);
    } catch (err) {
      toast.error(`確定失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>個別説明日程の確定</DialogTitle>
          <DialogDescription>
            候補日から1枠を選択して確定します。確定するとその枠は予約済みとなり、以降の受付はクローズされます。
            {saFilterActive && (
              <>
                <br />
                この商談は{saName ? `「${saName}」` : 'SA'}
                が担当のため、その担当者の枠のみ表示しています。
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {mode === 'additional' && (
          <label className="flex items-center gap-2 text-xs text-[var(--text-muted)] cursor-pointer">
            <input
              type="checkbox"
              checked={includePast}
              onChange={(e) => {
                setIncludePast(e.target.checked);
                setSelected(null);
              }}
              className="size-3.5"
            />
            過去の枠も表示する（既に実施した回を後から追加・切り出す場合）
          </label>
        )}

        {salesAgentUid && (
          <div className="flex items-center justify-between gap-2 text-xs">
            <span className="text-[var(--text-muted)]">
              {saFilterActive
                ? `担当SAの枠: ${filtered.length}件`
                : `全担当者の枠を表示中: ${filtered.length}件`}
            </span>
            <button
              type="button"
              className="text-[var(--accent)] hover:underline"
              onClick={() => {
                setShowAll((v) => !v);
                setSelected(null);
              }}
            >
              {saFilterActive ? '全担当者の枠を表示' : '担当SAの枠のみ表示'}
            </button>
          </div>
        )}

        {isLoading ? (
          <Spinner />
        ) : sorted.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)] py-4">
            {saFilterActive
              ? '担当SAが登録した受付可能な枠がありません。担当SAに枠の登録を依頼するか、「全担当者の枠を表示」から選択してください。'
              : '受付可能な枠がありません。スケジュールタブで枠を作成してください。'}
          </p>
        ) : (
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {sorted.map((s) => {
              const isCand = candidateScheduleIds?.includes(s.id) ?? false;
              const isSelected = selected === s.id;
              return (
                <label
                  key={s.id}
                  className={`flex items-center gap-2 rounded border px-3 py-2 cursor-pointer text-sm ${
                    isSelected
                      ? 'border-[var(--accent)] bg-[var(--accent-subtle)]'
                      : 'border-[var(--border)] hover:bg-[var(--bg-hover)]'
                  }`}
                >
                  <input
                    type="radio"
                    name="briefing_schedule"
                    checked={isSelected}
                    onChange={() => {
                      setSelected(s.id);
                      // 単独スタイル枠はその形式で確定、両方可は下で選択させる
                      setSelectedStyle(s.style === 'both' ? null : s.style);
                    }}
                    className="size-4"
                  />
                  <span className="tabular-nums">
                    {formatJpDate(s.date)} {s.startTime}〜{s.endTime}
                  </span>
                  <span className="text-xs text-[var(--text-muted)]">
                    · {BRIEFING_STYLE_LABEL[s.style]}
                  </span>
                  {s.location && (
                    <span className="text-xs text-[var(--text-muted)]">
                      @ {s.location}
                    </span>
                  )}
                  {!saFilterActive && assigneeName(s.assigneeUid) && (
                    <span className="text-xs text-[var(--text-muted)]">
                      · 担当: {assigneeName(s.assigneeUid)}
                    </span>
                  )}
                  {isCand && (
                    <span className="text-xs text-[var(--accent)] ml-auto">
                      ✓ 申込時の候補
                    </span>
                  )}
                  <span className="text-xs text-[var(--text-muted)] ml-auto tabular-nums">
                    受入{s.capacity}名
                  </span>
                </label>
              );
            })}
          </div>
        )}

        {needsStyleChoice && (
          <div className="rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-2.5 space-y-1.5">
            <div className="text-xs font-medium text-[var(--text-primary)]">
              開催形式を選択してください（この枠は「両方可」です）
            </div>
            <div className="flex items-center gap-4">
              {(['online', 'onsite'] as const).map((st) => (
                <label
                  key={st}
                  className="flex items-center gap-1.5 text-sm cursor-pointer"
                >
                  <input
                    type="radio"
                    name="briefing_style"
                    className="size-4"
                    checked={selectedStyle === st}
                    onChange={() => setSelectedStyle(st)}
                  />
                  {BRIEFING_STYLE_LABEL[st]}
                </label>
              ))}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={confirmMutation.isPending}
          >
            キャンセル
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleConfirm}
            disabled={
              !selected ||
              (needsStyleChoice && !selectedStyle) ||
              confirmMutation.isPending
            }
          >
            {confirmMutation.isPending ? '確定中...' : '日程を確定'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
